import React, { FC, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

import styles from './modal.module.scss';
import { Spinner } from '../spinner/spinner';

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  dialogIsOpen: boolean;
  closeDialog?: () => void;
  handleDefaultAction?: () => void;
  ariaLabelName?: string;
  contentClassName?: string;
  customSizeClassName?: string;
  dataTestId?: string;
  showCloseButton?: boolean;
  isLoading?: boolean;
  focusTrapEnabled?: boolean;
  closeOnClickOutside?: boolean;
}

const crossIcon = getDesignSystemIcon('icon-cross-16.svg');

export const Modal: FC<React.PropsWithChildren<ModalProps>> = ({
  dialogIsOpen,
  closeDialog,
  handleDefaultAction,
  ariaLabelName,
  title,
  children,
  contentClassName,
  customSizeClassName,
  dataTestId,
  showCloseButton = true,
  isLoading = false,
  focusTrapEnabled = false,
  closeOnClickOutside = true,
}) => {
  const { t } = useTranslation('shared');
  const modalPanelRef = useRef<HTMLInputElement>(null);

  // Disable scrolling when the modal is open
  useEffect(() => {
    const [blockScroll, unblockScroll] = [
      () => document.body.classList.add(styles.noscroll),
      () => document.body.classList.remove(styles.noscroll),
    ];

    dialogIsOpen ? blockScroll() : unblockScroll();

    return unblockScroll;
  }, [dialogIsOpen]);

  // Focus on the modal
  useEffect(() => {
    if (modalPanelRef !== null && modalPanelRef.current !== null) {
      modalPanelRef.current.focus();
    }
  }, [modalPanelRef, dialogIsOpen]);

  // Close the modal when clicking outside of it
  const handleClickOutside = useCallback(
    (event) => {
      if (modalPanelRef?.current?.contains && !modalPanelRef.current.contains(event.target)) {
        event.stopPropagation();
        closeDialog?.();
      }
    },
    [closeDialog, modalPanelRef]
  );

  useEffect(() => {
    if (closeOnClickOutside) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [handleClickOutside]);

  const handleKeydown = useCallback(
    (event) => {
      /* If esc is pressed modal should close */
      if (modalPanelRef?.current && modalPanelRef.current.contains(event.target) && event.key === 'Escape') {
        event.stopPropagation();
        closeDialog?.();
      }
      /* If enter is pressed, the default handleDefaultAction should run if provided */
      if (handleDefaultAction && event.key === 'Enter') {
        return handleDefaultAction();
      }
    },
    [closeDialog, modalPanelRef]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeydown, { capture: true });
  }, [handleKeydown]);

  const modalContent = dialogIsOpen ? (
    <div className={cx(styles.dialogOverlay, 'z-[33]')}>
      <FocusTrap active={focusTrapEnabled}>
        <div
          ref={modalPanelRef}
          aria-modal="true"
          aria-label={ariaLabelName}
          title={title}
          role="dialog"
          tabIndex={-1}
          className={cx(
            'relative left-0 top-0 mx-auto my-[10vh] bg-primary-white px-6 pb-6 pt-8 outline-none tablet:left-auto tablet:top-auto tablet:px-10 tablet:pb-10 tablet:pt-12',
            customSizeClassName || 'w-[90vw] max-w-3xl tablet:w-[50vw]',
            contentClassName
          )}
          data-testid={dataTestId}
        >
          {isLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-primary-white/75">
              <Spinner data-testid="modalLoadingSpinner" />
            </div>
          )}
          {children}
          {showCloseButton && (
            <button
              aria-label={t('global_button_close') as string}
              className="absolute right-0 top-0 h-8 w-8 cursor-pointer bg-primary-black p-2"
              onClick={closeDialog}
              data-testid="closeModal"
            >
              <svg className="h-4 w-4 text-primary-white">
                <use xlinkHref={`${crossIcon}#img`} />
              </svg>
            </button>
          )}
        </div>
      </FocusTrap>
    </div>
  ) : null;

  // Render the modal in a portal if we are in the browser
  if (typeof window !== 'undefined') {
    let portalRoot = document.getElementById('modal-portal');

    if (portalRoot === null) {
      portalRoot = document.body.appendChild(document.createElement('div'));
      portalRoot.setAttribute('id', 'modal-portal');
    }

    return ReactDOM.createPortal(modalContent, portalRoot);
  } else {
    return modalContent;
  }
};
